import { useState, useMemo } from "react"
import { Box, Typography, Paper, IconButton, Tooltip } from "@mui/material"
import { ContentCopy } from "@mui/icons-material"
import { observer } from "mobx-react"

import { ConfigurableDynamicFormFieldsStore } from "src/components/ConfigurableDynamicFormFields/store"
import { useStore } from "src/store/lib/useStore"
import { getSectionFieldName } from "src/components/ConfigurableDynamicFormFields/helpers/fieldNameHelpers"

interface IPublicAgentCodeSnippetsProps {
    sectionName: string
    agentKey?: string
}

export const PublicAgentCodeSnippets = observer(
    ({ sectionName, agentKey }: IPublicAgentCodeSnippetsProps) => {
        const store = useStore(ConfigurableDynamicFormFieldsStore)
        const [copiedSnippet, setCopiedSnippet] = useState<string | null>(null)

        // Find auto_uuid field value
        const autoUuidValue = useMemo(() => {
            if (agentKey !== undefined && agentKey !== "") return agentKey

            // Look for auto_uuid field in the current section
            const autoUuidField = store.configurableFields
                .find((field) => field.name === sectionName)
                ?.configurable_fields?.find(
                    (field) => field.type === "auto_uuid",
                )

            if (autoUuidField !== undefined) {
                const formFieldKey = getSectionFieldName(
                    sectionName,
                    autoUuidField.name,
                )
                const fieldValue = store.form.get(formFieldKey) as string
                return fieldValue !== undefined && fieldValue !== ""
                    ? fieldValue
                    : ""
            }
            return ""
        }, [store.configurableFields, store.form, sectionName, agentKey])

        const handleCopy = async (text: string, snippetType: string) => {
            try {
                await navigator.clipboard.writeText(text)
                setCopiedSnippet(snippetType)
                setTimeout(() => setCopiedSnippet(null), 2000)
            } catch (err) {
                // Silent fail for clipboard copy errors
            }
        }

        // Don't render if no UUID value
        if (autoUuidValue === "") {
            return null
        }

        const basicSnippet = `<script src="https://storage.googleapis.com/omni.avy.se/chat_bot_widget.js" data-flutter-url="https://public-davy.stage.avy.io/?public_agent_key=${autoUuidValue}"></script>`

        const CodeBlock = ({
            code,
            snippetType,
        }: {
            code: string
            snippetType: string
        }) => (
            <Paper
                variant="outlined"
                sx={{
                    position: "relative",
                    backgroundColor: "grey.900",
                    color: "common.white",
                    fontFamily: "monospace",
                    fontSize: "0.875rem",
                    overflow: "hidden",
                }}
            >
                <Box sx={{ p: 2, pr: 6 }}>
                    <pre style={{ margin: 0, whiteSpace: "pre-wrap" }}>
                        {code}
                    </pre>
                </Box>
                <Tooltip
                    title={
                        copiedSnippet === snippetType ? "Copied!" : "Copy code"
                    }
                >
                    <IconButton
                        onClick={() => handleCopy(code, snippetType)}
                        sx={{
                            position: "absolute",
                            top: 8,
                            right: 8,
                            color: "common.white",
                            "&:hover": {
                                backgroundColor: "rgba(255, 255, 255, 0.1)",
                            },
                        }}
                        size="small"
                    >
                        <ContentCopy fontSize="small" />
                    </IconButton>
                </Tooltip>
            </Paper>
        )

        return (
            <Box sx={{ mt: 3 }}>
                <Typography variant="h6" gutterBottom>
                    Website Integration Code
                </Typography>

                <Box sx={{ mb: 3 }}>
                    <Typography variant="body2" color="text.secondary" mb={1}>
                        Add this code to your website's HTML:
                    </Typography>
                    <CodeBlock code={basicSnippet} snippetType="basic" />
                </Box>
            </Box>
        )
    },
)
